import React, { useEffect, useState } from 'react';
import { CheckCircle2Icon, CloudIcon, CloudOffIcon, ClipboardCopyIcon, DownloadIcon, Loader2Icon, TriangleAlertIcon, UploadCloudIcon, UploadIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { downloadWebsiteBackup, moveLocalContentToCloud, type WebsiteContent } from '../../data/website';
import { getCloudStatus, onCloudStatusChange, type CloudStatus } from '../../data/cloudStatus';
interface CloudCardProps {
  draft: WebsiteContent;
  onRestore: (file: File | undefined) => Promise<void>;
  onCopy: () => Promise<void>;
  copied: boolean;
}

/**
 * Shows whether the site is saved to the band's own database, and offers the
 * one-time move for content that was typed in before the cloud was connected.
 */
export function CloudCard({
  draft,
  onRestore,
  onCopy,
  copied
}: CloudCardProps) {
  const [status, setStatus] = useState<CloudStatus>(() => getCloudStatus());
  const [moving, setMoving] = useState(false);
  const [result, setResult] = useState('');
  const [failed, setFailed] = useState(false);
  useEffect(() => onCloudStatusChange(setStatus), []);
  const move = async () => {
    setMoving(true);
    setResult('');
    const outcome = await moveLocalContentToCloud();
    setMoving(false);
    setFailed(Boolean(outcome.error));
    setResult(outcome.error ? outcome.error : outcome.photos ? `Moved your content and ${outcome.photos} photo${outcome.photos === 1 ? '' : 's'} to the cloud. Every device now shows this version.` : 'Moved your content to the cloud. Every device now shows this version.');
  };
  const tone = {
    synced: {
      icon: CheckCircle2Icon,
      className: 'text-emerald-300',
      label: 'Saved to the cloud'
    },
    saving: {
      icon: Loader2Icon,
      className: 'text-[#e8a369] animate-spin',
      label: 'Saving…'
    },
    loading: {
      icon: Loader2Icon,
      className: 'text-zinc-400 animate-spin',
      label: 'Loading…'
    },
    error: {
      icon: TriangleAlertIcon,
      className: 'text-rose-300',
      label: 'Not saved to the cloud'
    },
    'local-only': {
      icon: CloudOffIcon,
      className: 'text-warn',
      label: 'This device only'
    },
    idle: {
      icon: CloudIcon,
      className: 'text-zinc-500',
      label: 'Connecting…'
    }
  }[status.state];
  const Icon = tone.icon;
  return <Card className="p-4" data-id="element-916" data-mp="2w24drkomcg45">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between" data-id="element-917" data-mp="2w5igwv53wtcn">
        <div data-id="element-918" data-mp="2iapt79f2c6uz">
          <h3 className="flex items-center gap-2 text-[13.5px] font-semibold text-white" data-id="element-919" data-mp="2i9y0c6cuon8e">
            <Icon className={`h-4 w-4 ${tone.className}`} data-id="element-920" data-mp="2n4u23y7w2oyp" />
            {tone.label}
          </h3>
          <p className="mt-1 max-w-lg text-[12px] leading-relaxed text-zinc-400" data-id="element-921" data-mp="28brwhj7hh70w">
            {status.message || 'Every edit saves here automatically, so your site stays the same on every device. Photos upload to cloud storage with no size limit. Publish when you want fans to see the changes.'}
          </p>
          {status.at && !status.message && <p className="mt-1 text-[11px] text-zinc-600" data-id="element-922" data-mp="1kr93sfarkgbk">Last saved {new Date(status.at).toLocaleTimeString()}</p>}
        </div>
        {status.state !== 'synced' && <div className="flex shrink-0 flex-wrap gap-2" data-id="element-923" data-mp="1treb6pfyjepq">
            <Button size="sm" onClick={() => void move()} disabled={moving} data-id="element-924" data-mp="1tot8xzbqywrg">
              <UploadCloudIcon className="h-3.5 w-3.5" data-id="element-925" data-mp="1po99uq7m5j2o" /> {moving ? 'Moving…' : 'Move my content to the cloud'}
            </Button>
          </div>}
      </div>

      {result && <p className={`mt-3 text-[12px] leading-relaxed ${failed ? 'text-rose-300' : 'text-emerald-300'}`} data-id="element-926" data-mp="1ypplt2e2tt90">{result}</p>}

      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-white/[0.06] pt-3" data-id="element-927" data-mp="2dgy74zay4sgc">
        <span className="text-[11px] text-zinc-600" data-id="element-928" data-mp="28i51tu5tc7r9">Extra safety net</span>
        <Button variant="secondary" size="sm" onClick={() => downloadWebsiteBackup(draft)} data-id="element-929" data-mp="2mgcuhpvtgmwm">
          <DownloadIcon className="h-3.5 w-3.5" data-id="element-930" data-mp="2hi1pbsggv0kq" /> Download backup
        </Button>
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-white/[0.1] px-3 py-2 text-[12.5px] font-medium text-zinc-300 transition-colors duration-150 ease-out hover:border-accent/40 hover:text-white" data-id="element-931" data-mp="fqhyorzg6kce">
          <UploadIcon className="h-3.5 w-3.5" data-id="element-932" data-mp="pjgx11kqxp97" /> Restore
          <input type="file" accept="application/json" className="hidden" onChange={event => {
          void onRestore(event.target.files?.[0]);
          event.target.value = '';
        }} data-id="element-933" data-mp="thwiv253crl1" />
        </label>
        <Button variant="secondary" size="sm" onClick={() => void onCopy()} data-id="element-934" data-mp="5svfhtb486mm">
          <ClipboardCopyIcon className="h-3.5 w-3.5" data-id="element-935" data-mp="asggsadrcr2i" /> {copied ? 'Copied' : 'Copy as text'}
        </Button>
        {status.state === 'synced' && <Button variant="secondary" size="sm" onClick={() => void move()} disabled={moving} data-id="element-936" data-mp="1h7qtre5uo62n">
            <UploadCloudIcon className="h-3.5 w-3.5" data-id="element-937" data-mp="1m6btzdl3qpr3" /> {moving ? 'Uploading…' : 'Force re-upload'}
          </Button>}
      </div>
    </Card>;
}